/**
 * HH:MM entry that forgives how people actually type hours.
 *
 * Lifted out of the work order timesheet (Pages/WO/Components/timesheet/
 * DayTechItem.jsx) so the service ticket editor enters hours the same way. The
 * parsing rules are the interesting part and they are shared, not copied.
 */

export const EMPTY_TIME = "00:00";

/**
 * Normalize an HH:MM string. Anything malformed falls back to "00:00".
 * Only used for values we already believe are in HH:MM shape.
 */
export const normalizeTimeValue = (value) => {
  if (!value || typeof value !== "string") return EMPTY_TIME;
  const trimmed = value.trim();
  if (!trimmed || trimmed === "__:__") return EMPTY_TIME;

  const parts = trimmed.split(":");
  if (parts.length !== 2) return EMPTY_TIME;

  const hours = Number(parts[0]);
  const minutes = Number(parts[1]);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return EMPTY_TIME;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return EMPTY_TIME;

  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

/**
 * Parse whatever the user typed into a canonical HH:MM string.
 * Supports:
 *   ""           -> "00:00"
 *   "1"          -> "01:00"     (whole hours)
 *   "10"         -> "10:00"
 *   "13:45"      -> "13:45"     (already formatted)
 *   "1345"       -> "13:45"     (raw 4 digits)
 *   ":15"        -> "00:15"     (minutes only via colon)
 *   ".5" / ".25" -> "00:30" / "00:15"  (decimal hours)
 *   "1.5"        -> "01:30"     (decimal hours > 1)
 * Anything it can't make sense of -> "00:00".
 */
export const parseUserTime = (raw) => {
  if (raw === null || raw === undefined) return EMPTY_TIME;
  const value = String(raw).trim();
  if (!value || value === "__:__") return EMPTY_TIME;

  // Case 1: starts with a colon -> minutes only, e.g. ":15"
  if (value.startsWith(":")) {
    const minutes = Number(value.slice(1));
    if (Number.isNaN(minutes) || minutes < 0 || minutes > 59) return EMPTY_TIME;
    return `00:${String(minutes).padStart(2, "0")}`;
  }

  // Case 2: decimal hours, e.g. ".5", ".25", "1.75", "2.5"
  if (value.includes(".")) {
    const asFloat = Number(value);
    if (Number.isNaN(asFloat) || asFloat < 0) return EMPTY_TIME;
    const totalMinutes = Math.round(asFloat * 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours > 23) return EMPTY_TIME;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  }

  // Case 3: explicit HH:MM with a colon somewhere in the middle
  if (value.includes(":")) {
    const parts = value.split(":");
    if (parts.length !== 2) return EMPTY_TIME;
    const hours = Number(parts[0] || "0");
    const minutes = Number(parts[1] || "0");
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return EMPTY_TIME;
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return EMPTY_TIME;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  }

  // Case 4: pure digits, no colon, no dot
  if (/^\d+$/.test(value)) {
    if (value.length <= 2) {
      // "1" -> 01:00, "10" -> 10:00
      const hours = Number(value);
      if (hours > 23) return EMPTY_TIME;
      return `${String(hours).padStart(2, "0")}:00`;
    }
    if (value.length === 3) {
      // "130" -> 1:30
      const hours = Number(value.slice(0, 1));
      const minutes = Number(value.slice(1));
      if (hours > 23 || minutes > 59) return EMPTY_TIME;
      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
    }
    if (value.length === 4) {
      // "1345" -> 13:45
      const hours = Number(value.slice(0, 2));
      const minutes = Number(value.slice(2));
      if (hours > 23 || minutes > 59) return EMPTY_TIME;
      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
    }
    return EMPTY_TIME;
  }

  return EMPTY_TIME;
};

export const isNormalizedTimeValue = (value) => {
  if (typeof value !== "string") return false;
  if (!/^\d{2}:\d{2}$/.test(value)) return false;
  return normalizeTimeValue(value) === value;
};

/**
 * The field holds whatever is being typed; it is only parsed on the way out.
 *
 * `onChange` receives the raw text as it is typed and the normalized value on
 * blur. `onCommit` is the normalized value alone, for callers that act on it.
 * `blankOnEmpty` keeps an emptied field empty instead of collapsing it to
 * "00:00" — the service ticket prints an unset cell as a dash.
 */
export default function TimeInput({
  value,
  onChange,
  onCommit,
  onKeyDown,
  blankOnEmpty = false,
  className = "",
  placeholder,
  ...rest
}) {
  /**
   * When the user focuses the field:
   *  - If it's "00:00", wipe it so the cursor blinks in an empty box.
   *  - Otherwise select all so typing replaces the value (and triple-click
   *    still selects all, as expected).
   */
  const handleFocus = (event) => {
    if (value === EMPTY_TIME || value === "") {
      onChange("");
      return;
    }
    // Defer so the select happens after focus settles.
    const input = event.target;
    setTimeout(() => {
      try {
        input.select();
      } catch (_err) {
        /* no-op */
      }
    }, 0);
  };

  const handleChange = (event) => {
    let nextValue = event.target.value.slice(0, 6);
    const previousValue = value || "";
    const isDeleting = nextValue.length < previousValue.length;

    // If the user typed a "." right after an auto-inserted colon
    // (e.g. "12:" + "." -> "12:."), strip the colon so "12.5" parses
    // as decimal hours.
    if (nextValue.includes(":.")) {
      nextValue = nextValue.replace(":.", ".");
    }

    // When deleting, also strip a trailing colon so backspace can get past the
    // auto-inserted ":" (e.g. "12:25" -> "12:2" -> "12:" -> this strips to
    // "12", next backspace -> "1").
    if (isDeleting && nextValue.endsWith(":")) {
      nextValue = nextValue.slice(0, -1);
    }

    // Auto-insert a colon after two digits, but only when the user is typing
    // forward (not deleting) and the value is pure digits. Leaves ".5", ":15",
    // "1.5", and mid-delete states alone.
    if (!isDeleting && /^\d+$/.test(nextValue) && nextValue.length >= 2) {
      nextValue = nextValue.length === 2 ? `${nextValue}:` : `${nextValue.slice(0, 2)}:${nextValue.slice(2, 4)}`;
    }

    onChange(nextValue);
  };

  const handleBlur = () => {
    const normalized = value === "" && blankOnEmpty ? "" : parseUserTime(value === "" ? EMPTY_TIME : value);
    if (normalized !== value) onChange(normalized);
    if (onCommit) onCommit(normalized);
  };

  return (
    <input
      value={value ?? ""}
      onFocus={handleFocus}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      className={className}
      {...rest}
    />
  );
}
